import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { Loader2, Plus, X, Sparkles, Quote, ImageIcon, Target, Trash2, Clock, Flame, Upload } from "lucide-react";
import { daysUntil, visualFor, COUNTDOWN_PHASE_STYLES } from "@/lib/visionVisuals";

export default function Vision() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "quote", content: "", image_url: "", target_date: "" });
  const [future, setFuture] = useState(null);
  const [loadingFuture, setLoadingFuture] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [genPrompt, setGenPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => { const { data } = await api.get("/vision/board"); setD(data); }, []);
  useEffect(() => { load(); }, [load]);

  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const absolutize = (u) => (u && u.startsWith("/api/") ? `${backendBase}${u}` : u);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file.");
    if (file.size > 10 * 1024 * 1024) return toast.error("Image exceeds 10 MB limit.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/vision/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setUploading(false); e.target.value = ""; }
  };

  const generateWithAI = async () => {
    const prompt = genPrompt.trim();
    if (!prompt) return toast.error("Describe the vision you want to see.");
    setGenerating(true);
    try {
      const { data } = await api.post("/vision/generate", { prompt });
      setForm((f) => ({ ...f, image_url: data.url, content: f.content || prompt }));
      toast.success("Vision image generated.");
    } catch (err) {
      if (!err.handledUpgrade) toast.error(apiError(err.response?.data?.detail, "Generation failed."));
    } finally { setGenerating(false); }
  };

  const addItem = async () => {
    if (!form.content && !form.image_url) return toast.error("Add content or an image.");
    await api.post("/vision/items", form);
    setForm({ type: "quote", content: "", image_url: "", target_date: "" });
    setOpen(false); load();
  };
  const del = async (id) => { await api.delete(`/vision/items/${id}`); load(); };

  const genFuture = async () => {
    setLoadingFuture(true);
    try { const { data } = await api.post("/vision/future-self"); setFuture(data); }
    catch (e) { !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoadingFuture(false); }
  };

  if (!d) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF7E67]" /></div>;

  return (
    <div className="space-y-8" data-testid="vision-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sunrise">Vision Board</p>
          <h1 className="mt-2 font-nunito font-extrabold tracking-tight text-4xl text-[#2D3748] sm:text-5xl">See It. Become It.</h1>
        </div>
        <button onClick={() => setOpen(true)} data-testid="add-vision-btn"
          className="flex items-center gap-2 rounded-full bg-sunrise px-5 py-3 text-sm font-bold tracking-wide text-[#2D3748] hover:scale-105"><Plus className="h-4 w-4" /> Add</button>
      </div>

      <div className="rounded-3xl border border-[#FFE8E3] bg-white p-6">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-nunito text-sm font-bold tracking-wide text-[#2D3748]"><Sparkles className="h-4 w-4 text-[#FF7E67]" /> Future Self</h2>
          <button onClick={genFuture} disabled={loadingFuture} data-testid="future-self-btn" className="rounded-full border border-[#FFE8E3] px-4 py-1.5 text-xs text-[#2D3748] hover:border-[#FF7E67]">
            {loadingFuture ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate with AI"}
          </button>
        </div>
        {future && <p className="mt-4 text-lg leading-relaxed text-[#2D3748]">{future.statement}</p>}
      </div>

      {d.items.length === 0 && <p className="text-center text-[#718096]">Your board is empty — add images, quotes, and goals to visualize your future.</p>}

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {d.items.map((it) => <VisionCard key={it.id} it={it} onDelete={del} />)}
      </div>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#2D3748]/50 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md space-y-3 rounded-3xl border border-[#FFE8E3] bg-white p-7" onClick={(e) => e.stopPropagation()} data-testid="vision-modal">
            <div className="flex items-center justify-between"><h3 className="font-nunito text-xl font-bold text-[#2D3748]">Add to Board</h3><button onClick={() => setOpen(false)}><X className="h-5 w-5 text-[#718096]" /></button></div>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="h-11 w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 capitalize text-[#2D3748] outline-none focus:border-[#FF7E67]">
              <option value="quote">Quote</option><option value="goal">Goal</option><option value="image">Image</option>
            </select>
            {form.type === "image" && (
              <div className="space-y-2">
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={onPickFile} className="hidden"
                  data-testid="vision-file-input" />
                <button type="button" onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="vision-upload-btn"
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#FFE8E3] bg-[#FFF9F5] py-6 font-nunito text-sm font-bold text-[#C2410C] transition-all hover:border-[#FF7E67] hover:bg-[#FFF4EF] disabled:opacity-60">
                  {uploading
                    ? <><Loader2 className="h-5 w-5 animate-spin" /> Uploading…</>
                    : <><Upload className="h-5 w-5" strokeWidth={2.5} /> Choose an image from your device</>}
                </button>
                {form.image_url && (
                  <div className="relative">
                    <img src={absolutize(form.image_url)} alt="preview"
                         className="max-h-48 w-full rounded-2xl border border-[#FFE8E3] object-cover" />
                    <button type="button" onClick={() => setForm((f) => ({ ...f, image_url: "" }))}
                      className="absolute right-2 top-2 rounded-full bg-[#2D3748]/70 p-1.5 text-white hover:bg-[#2D3748]">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                <div className="text-center text-[10px] font-bold uppercase tracking-widest text-[#A0AEC0]">— OR generate with AI —</div>
                <div className="space-y-2 rounded-2xl border border-[#FFE8E3] bg-gradient-to-br from-[#FFF9F5] to-[#FFF4E6] p-3">
                  <textarea value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)}
                    rows={2} placeholder="Describe your vision (e.g. me crossing the marathon finish line at sunrise)"
                    data-testid="vision-ai-prompt"
                    className="w-full resize-none rounded-xl border border-[#FFE8E3] bg-white p-2.5 text-sm text-[#2D3748] outline-none focus:border-[#FF7E67]" />
                  <button type="button" onClick={generateWithAI} disabled={generating || !genPrompt.trim()}
                    data-testid="vision-ai-generate-btn"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-brand py-2.5 font-nunito text-sm font-bold text-black shadow-glow-coral transition-all disabled:opacity-60">
                    {generating
                      ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating your vision…</>
                      : <><Sparkles className="h-4 w-4" /> Generate with AI</>}
                  </button>
                </div>
                <div className="text-center text-[10px] font-bold uppercase tracking-widest text-[#A0AEC0]">— OR paste a URL —</div>
                <input value={form.image_url.startsWith("/api/") ? "" : form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="Paste an image URL (https://…)"
                  data-testid="vision-image-url"
                  className="h-11 w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
              </div>
            )}
            <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={3} placeholder={form.type === "quote" ? "An inspiring quote…" : form.type === "goal" ? "A goal or caption…" : "Caption (optional)"}
              className="w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] p-3 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
            {form.type === "goal" && (
              <div>
                <label className="mb-1 block font-nunito text-xs font-bold uppercase tracking-wide text-[#718096]">Target date (for the countdown)</label>
                <input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                  className="h-11 w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
              </div>
            )}
            <button onClick={addItem} data-testid="save-vision-btn" className="w-full rounded-full bg-sunrise py-3 font-bold text-white shadow-glow-coral">Add to Board</button>
          </div>
        </div>
      )}
    </div>
  );
}

function VisionCard({ it, onDelete }) {
  const countdown = daysUntil(it.target_date);
  const { image, gradient } = visualFor(it);
  const phase = countdown ? COUNTDOWN_PHASE_STYLES[countdown.phase] : null;

  // Different layouts for goal/image vs quote:
  //   - goal or image: big hero card with photo/gradient background + countdown
  //   - quote:         elegant typography card
  if (it.type === "quote") {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="group relative flex min-h-[220px] flex-col justify-between overflow-hidden rounded-[2rem] border border-[#FFE8E3] bg-gradient-to-br from-white via-[#FFF9F5] to-[#FFF4E6] p-6 shadow-warm">
        <Quote className="absolute right-4 top-4 h-10 w-10 text-[#FFE8E3]" strokeWidth={2} />
        <div>
          <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#C2410C]">Mantra</span>
          <p className="mt-3 font-nunito text-xl font-medium italic leading-snug text-[#2D3748]">
            &ldquo;{it.content}&rdquo;
          </p>
        </div>
        <button onClick={() => onDelete(it.id)}
          className="absolute right-3 top-3 z-20 rounded-full bg-white/60 p-1.5 opacity-0 shadow transition-opacity group-hover:opacity-100"
          aria-label="Remove">
          <Trash2 className="h-3.5 w-3.5 text-[#DC2626]" />
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="group relative flex min-h-[280px] flex-col justify-end overflow-hidden rounded-[2rem] shadow-warm-lg"
      data-testid={`vision-card-${it.id}`}>
      {/* Background layers */}
      {image ? (
        <img src={image} alt="" loading="lazy"
             className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0"
             style={{ background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})` }} />
      )}
      {/* Dark gradient overlay for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/85 via-[#0F172A]/40 to-transparent" />

      {countdown && (
        <div className={`absolute right-4 top-4 flex items-center gap-2 rounded-2xl px-4 py-2 ring-2 backdrop-blur ${phase.pill} ${phase.ring}/40`}
             data-testid={`countdown-${it.id}`}>
          <Clock className="h-4 w-4" strokeWidth={2.5} />
          <div className="leading-none">
            <div className="text-2xl font-extrabold">
              {countdown.days > 0 ? countdown.days : Math.abs(countdown.days)}
            </div>
            <div className="text-[9px] font-bold uppercase tracking-widest opacity-90">
              {countdown.phase === "today" ? "GO DAY"
                : countdown.phase === "past" ? "days past"
                : countdown.days === 1 ? "day" : "days"}
            </div>
          </div>
        </div>
      )}

      <button onClick={() => onDelete(it.id)}
        className="absolute left-3 top-3 z-20 rounded-full bg-white/20 p-1.5 opacity-0 backdrop-blur transition-opacity group-hover:opacity-100"
        aria-label="Remove">
        <Trash2 className="h-3.5 w-3.5 text-white" />
      </button>

      <div className="relative z-10 p-6 text-white">
        <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-3 py-0.5 font-nunito text-[10px] font-bold uppercase tracking-wider backdrop-blur">
          {it.type === "image" ? <ImageIcon className="h-3 w-3" /> : <Target className="h-3 w-3" />} {it.type}
        </span>
        {it.content && (
          <h3 className="mt-3 font-nunito text-2xl font-extrabold leading-tight drop-shadow-sm">
            {it.content}
          </h3>
        )}
        {it.target_date && (
          <p className="mt-2 flex items-center gap-1.5 font-quicksand text-xs text-white/80">
            <Flame className="h-3.5 w-3.5 text-[#FFB347]" strokeWidth={2.5} />
            Target · {new Date(`${it.target_date}T00:00`).toLocaleDateString(undefined,
              { month: "short", day: "numeric", year: "numeric" })}
          </p>
        )}
      </div>
    </motion.div>
  );
}
